import { HttpClient } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { TokenTypeEnum } from 'authentication/enums/token-type.enum';
import { TokenService } from 'authentication/services/token.service';
import { iceSizeIdMapping, iceSizeTypeMapping } from 'dashboard/constants/visuals-dashboard/ice-size-types';
import { widgetsMapping } from 'dashboard/constants/visuals-dashboard/widget-component-mapping';
import { IceDashboardDisplayStateEnum } from 'dashboard/enums/visuals/ice-dashboard-display-state.enum';
import { IceDashboarPeriodPickerValueToOptionsMap } from 'dashboard/enums/visuals/ice-dashboard-period-picker.enum';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { VisualsDashboardWidgetEnum } from 'dashboard/enums/visuals/visuals-dashboard-widgets.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { IceDashboardWidgetResponse } from 'dashboard/interfaces/visuals/ice-dashboard-widget-response.interface';
import { IceListWidgetResponse } from 'dashboard/interfaces/visuals/ice-list-widget-response.interface';
import {
    DashboardListWidget,
    HistoryWidgetSettings,
    ImageTrackerDistributionSettings,
    VisualDashboardWidgetSize,
    isHistoryWidgetSettings,
    isImageTrackerDistributionSettings,
} from 'dashboard/interfaces/visuals/list-widget.interface';
import { VisualDashboardDistributor } from 'dashboard/interfaces/visuals/visual-dashboard-distributor.interface';
import { camelCase } from 'lodash-es';

import { Observable, Subject, map, switchMap, tap, throwError } from 'rxjs';

const API_URL = environment.iceDashboardUrl;

@Injectable({
    providedIn: 'root',
})
export class VisualsDashboardService {
    private newWidgetCounter = 0;

    loadWidgetsList$ = new Subject<void>();
    loadDashboardState$ = new Subject<void>();

    dashboard = signal<DashboardWidget[]>([]);
    loadedDashboardData = signal<boolean>(false);

    selectedWidgetsForAdding = signal<
        { widget: DashboardListWidget; selectedSize: VisualsDashboardWidgetSizingEnum; selectedPeriod: number | null }[]
    >([]);

    rawWidgetsList = toSignal(
        this.loadWidgetsList$.pipe(
            switchMap(() =>
                this.http.get<IceListWidgetResponse[]>(`${API_URL}/widgets/configuration/all`, {
                    headers: {
                        'X-Token-Type': TokenTypeEnum.ICE_DASHBOARD,
                    },
                }),
            ),
        ),
        { initialValue: [] },
    );

    widgetTypeIdToEnum = computed(() => {
        const mapping: Record<number, VisualsDashboardWidgetEnum> = {};
        const rawWidgets = this.rawWidgetsList();
        if (!rawWidgets) {
            return mapping;
        }
        rawWidgets.forEach((widget) => {
            mapping[widget.typeId] = widget.title as VisualsDashboardWidgetEnum;
        });
        return mapping;
    });

    mappedWidgetsList = computed<DashboardListWidget[]>(() => {
        const rawWidgets = this.rawWidgetsList();
        this.onLangChange(); // Trigger recomputation on language change
        const mappedWidgets = rawWidgets.map((widget) => this.mapIceListWidgetToDashboardListWidget(widget));
        return mappedWidgets;
    });

    rawDashboardState = toSignal(
        this.loadDashboardState$.pipe(
            switchMap(() =>
                this.http.get<IceDashboardWidgetResponse[]>(`${API_URL}/dashboard/get`, {
                    headers: {
                        'X-Token-Type': TokenTypeEnum.ICE_DASHBOARD,
                    },
                }),
            ),
            tap(() => {
                this.newWidgetCounter = 0;
                this.loadedDashboardData.set(true);
            }),
        ),
        { initialValue: [] },
    );

    mappedDashboardState = computed(() => {
        const rawState = this.rawDashboardState();
        const widgetsList = this.mappedWidgetsList();
        const widgetTypeIdToEnum = this.widgetTypeIdToEnum();
        if (!rawState || !widgetTypeIdToEnum) {
            return [];
        }

        const mappedState = rawState.map((widget) => this.mapIceDashboardWidgetToDashboardWidget(widget, widgetsList));
        const implementedWidgets = widgetsMapping;
        return mappedState.filter((widget) => implementedWidgets[widget.key]);
    });

    htmlIdToBackgroundStateOverrideMap = signal<Record<string, IceDashboardDisplayStateEnum>>({});
    htmlIdToAvailableDistributorsMap = signal<Record<string, VisualDashboardDistributor[]>>({});

    onLangChange = toSignal(this.translateService.onLangChange);

    constructor(
        private http: HttpClient,
        private translateService: TranslateService,
        private tokenService: TokenService,
    ) {}

    obtainDashboardTokenAsObservable(): Observable<string> {
        const identityToken = this.tokenService.identityToken();
        if (!identityToken) {
            return throwError(() => new Error('No identity token available'));
        }

        return this.http
            .post<{ token: string }>(
                `${API_URL}/auth/externalSignIn`,
                {},
                {
                    headers: {
                        'X-Token-Type': TokenTypeEnum.IDENTITY_TOKEN,
                        isBypassTokenInterceptorRetryRequest: 'true',
                    },
                },
            )
            .pipe(
                map((response) => response.token),
                tap((token: string) => {
                    this.tokenService.setIceDashboardToken(token);
                }),
            );
    }

    setHtmlIdBackgroundStateOverride(htmlId: string, state: IceDashboardDisplayStateEnum): void {
        this.htmlIdToBackgroundStateOverrideMap.update((current) => ({
            ...current,
            [htmlId]: state,
        }));
    }

    setAvailableDistributorsForHtmlId(htmlId: string, distributors: VisualDashboardDistributor[]): void {
        this.htmlIdToAvailableDistributorsMap.update((current) => ({
            ...current,
            [htmlId]: distributors,
        }));
    }

    saveDashboardState(widgets: DashboardWidget[]): void {
        const payload: IceDashboardWidgetResponse[] = widgets.map((widget) => this.mapDashboardWidgetToIceDashboardWidget(widget));
        this.http
            .post(`${API_URL}/dashboard/set`, payload, {
                headers: {
                    'X-Token-Type': TokenTypeEnum.ICE_DASHBOARD,
                },
            })
            .pipe(
                tap(() => {
                    this.loadDashboardState$.next();
                }),
            )
            .subscribe();
    }

    mapDashboardListWidgetToDashboardWidget(
        dashboardListWidget: DashboardListWidget,
        pickedSize: VisualsDashboardWidgetSizingEnum,
        isDefaultSize: boolean,
        selectedPeriod: number | null,
    ): DashboardWidget {
        const { rows, cols } = this.getRowsAndColsBySize(pickedSize);
        const allItems = this.mappedWidgetsList();
        const matchingWidget = allItems.find((item) => item.widgetId === dashboardListWidget.widgetId);

        const htmlId = `new-widget-${dashboardListWidget.widgetId}-${this.newWidgetCounter++}`;

        const item: DashboardWidget = {
            htmlId,
            originalCols: cols,
            originalRows: rows,
            iceDashboardWidget: this.mapIceListWidgetToIceDashboardWidget(dashboardListWidget, pickedSize, isDefaultSize, selectedPeriod),
            key: dashboardListWidget.key,
            component: null,
            selectedSize: pickedSize,
            rows: rows,
            cols: cols,
            x: 0,
            y: 0,
            availableSizes: matchingWidget?.sizes || [],
            translatedFullDescription: matchingWidget?.translatedFullDescription || '',
            selectedPeriodOptionValue: selectedPeriod,
            imageTrackerDistributionSettings:
                dashboardListWidget.defaultSettings && isImageTrackerDistributionSettings(dashboardListWidget.defaultSettings)
                    ? dashboardListWidget.defaultSettings
                    : null,
        };
        this.attachComponentToGridItem(item);
        return item;
    }

    mapIceListWidgetToIceDashboardWidget(
        iceListWidget: DashboardListWidget,
        pickedSize: VisualsDashboardWidgetSizingEnum,
        isDefaultSize: boolean,
        selectedPeriod: number | null,
    ): IceDashboardWidgetResponse {
        const { rows, cols } = this.getRowsAndColsBySize(pickedSize);

        const distributionSettings =
            iceListWidget.defaultSettings && isImageTrackerDistributionSettings(iceListWidget.defaultSettings)
                ? iceListWidget.defaultSettings
                : null;

        const iceWidget: IceDashboardWidgetResponse = {
            id: 0,
            widgetId: iceListWidget.widgetId,
            sizeTypeId: iceSizeIdMapping[pickedSize],
            positionX: 0,
            positionY: 0,
            settings: this.buildSettings(selectedPeriod, distributionSettings),
            title: null,
            sizeType: {
                rows: rows,
                cols: cols,
                isDefaultSize: isDefaultSize,
                sizeTypeId: iceSizeIdMapping[pickedSize],
                type: iceSizeTypeMapping[pickedSize],
            },
            widgetTypeId: iceListWidget.widgetTypeId,
            widgetType: {
                id: iceListWidget.widgetTypeId,
                name: '',
                canBeIncludedInPdfReport: false,
                communicationConfigs: [],
            },
            syncToken: null,
        };
        return iceWidget;
    }

    buildSettings(selectedPeriod: number | null, distributionSettings: ImageTrackerDistributionSettings | null): string | null {
        if (selectedPeriod === null && distributionSettings === null) {
            return null;
        }

        const settingsObject: Record<string, unknown> = {};

        if (selectedPeriod !== null) {
            settingsObject.optionValue = selectedPeriod;
            settingsObject.name = IceDashboarPeriodPickerValueToOptionsMap[selectedPeriod].capitalizeFirstLetter();
        }

        if (distributionSettings !== null) {
            settingsObject.isPhotos = distributionSettings.isPhotos;
            settingsObject.isListings = distributionSettings.isListings;
            settingsObject.distributorIds = distributionSettings.distributorIds;
        }

        return JSON.stringify(settingsObject);
    }

    mapDashboardWidgetToIceDashboardWidget(dashboardWidget: DashboardWidget): IceDashboardWidgetResponse {
        const iceWidget: IceDashboardWidgetResponse = {
            id: dashboardWidget.iceDashboardWidget.id,
            widgetId: dashboardWidget.iceDashboardWidget.widgetId,
            sizeTypeId: dashboardWidget.iceDashboardWidget.sizeTypeId,
            positionX: dashboardWidget.x,
            positionY: dashboardWidget.y,
            settings: this.buildSettings(dashboardWidget.selectedPeriodOptionValue, dashboardWidget.imageTrackerDistributionSettings),
            title: dashboardWidget.iceDashboardWidget.title,
            sizeType: dashboardWidget.iceDashboardWidget.sizeType,
            widgetTypeId: dashboardWidget.iceDashboardWidget.widgetTypeId,
            widgetType: dashboardWidget.iceDashboardWidget.widgetType,
            syncToken: dashboardWidget.iceDashboardWidget.syncToken,
        };
        return iceWidget;
    }

    mapIceListWidgetToDashboardListWidget(iceWidgetResponseItem: IceListWidgetResponse): DashboardListWidget {
        const camelCaseKey = camelCase(iceWidgetResponseItem.title);
        const translatedTitle = this.translateService.instant(`dashboard.visuals.widgets.${camelCaseKey}.title`);
        const defaultSettingsRaw = iceWidgetResponseItem.defaultSettings;
        let defaultSettings: HistoryWidgetSettings | ImageTrackerDistributionSettings | null = null;

        if (defaultSettingsRaw) {
            try {
                const parsedSettings = JSON.parse(defaultSettingsRaw);
                defaultSettings = parsedSettings;
            } catch (error) {
                console.error('Error parsing defaultSettings:', error);
            }
        }
        const item: DashboardListWidget = {
            widgetId: iceWidgetResponseItem.widgetId,
            widgetTypeId: iceWidgetResponseItem.typeId,
            key: iceWidgetResponseItem.title as VisualsDashboardWidgetEnum,

            sizes: iceWidgetResponseItem.widgetSizes.map((size) => {
                const sizeItem: VisualDashboardWidgetSize = {
                    size: this.getSizeBasedOnRowsAndCols(size.rows, size.cols),
                    isDefault: size.isDefaultSize,
                };
                return sizeItem;
            }),
            iceListWidget: iceWidgetResponseItem,
            searchableText: translatedTitle.toLowerCase(),
            defaultSettings: defaultSettings,
            translatedFullDescription: this.getFullTranslatedDescriptionForWidget(
                iceWidgetResponseItem.title as VisualsDashboardWidgetEnum,
            ),
        };
        return item;
    }

    mapIceDashboardWidgetToDashboardWidget(
        iceDashboardWidgetResponse: IceDashboardWidgetResponse,
        widgetList: DashboardListWidget[],
    ): DashboardWidget {
        const matchingWidget = widgetList.find((widget) => widget.widgetId === iceDashboardWidgetResponse.widgetId);
        const settings = iceDashboardWidgetResponse.settings;
        const parsedSettings = settings ? JSON.parse(settings) : null;
        const periodValue = parsedSettings && isHistoryWidgetSettings(parsedSettings) ? parsedSettings.optionValue : null;
        const keyByWidgetTypeId = this.widgetTypeIdToEnum()[iceDashboardWidgetResponse.widgetTypeId];
        const item: DashboardWidget = {
            widgetId: iceDashboardWidgetResponse.widgetId,
            widgetTypeId: iceDashboardWidgetResponse.widgetTypeId,
            iceDashboardWidget: iceDashboardWidgetResponse,
            htmlId: `widget-${iceDashboardWidgetResponse.id}-${iceDashboardWidgetResponse.widgetId}`,
            originalCols: iceDashboardWidgetResponse.sizeType.cols,
            originalRows: iceDashboardWidgetResponse.sizeType.rows,
            x: iceDashboardWidgetResponse.positionX,
            y: iceDashboardWidgetResponse.positionY,
            component: null,
            rows: iceDashboardWidgetResponse.sizeType.rows,
            cols: iceDashboardWidgetResponse.sizeType.cols,
            key: keyByWidgetTypeId,
            selectedSize: this.getSizeBasedOnRowsAndCols(
                iceDashboardWidgetResponse.sizeType.rows,
                iceDashboardWidgetResponse.sizeType.cols,
            ),
            availableSizes: matchingWidget?.sizes || [],
            translatedFullDescription: matchingWidget?.translatedFullDescription || '',
            selectedPeriodOptionValue: periodValue,
            imageTrackerDistributionSettings: parsedSettings && isImageTrackerDistributionSettings(parsedSettings) ? parsedSettings : null,
        };
        this.attachComponentToGridItem(item);
        return item;
    }

    getFullTranslatedDescriptionForWidget(widgetKey: VisualsDashboardWidgetEnum): string {
        const camelCaseKey = camelCase(widgetKey);
        const translation = this.translateService.instant(`dashboard.visuals.widgets.${camelCaseKey}.description`);

        if (typeof translation === 'string') {
            return translation;
        }

        if (typeof translation === 'object' && translation !== null) {
            const parts = [translation['part1'], translation['part2']].filter(Boolean);
            return parts.join(' ');
        }

        return '';
    }

    attachComponentToGridItem(itemComponent: DashboardWidget): DashboardWidget {
        if (itemComponent && !itemComponent.component) {
            const widgetType = widgetsMapping[itemComponent.key];
            if (widgetType) {
                itemComponent.component = widgetType;
            }
        }
        return itemComponent;
    }

    getSizeBasedOnRowsAndCols(rows: number, cols: number): VisualsDashboardWidgetSizingEnum {
        switch (true) {
            case cols === 1 && rows === 1:
                return VisualsDashboardWidgetSizingEnum.SMALL;
            case cols === 2 && rows === 1:
                return VisualsDashboardWidgetSizingEnum.MEDIUM_ROW;
            case cols === 3 && rows === 1:
                return VisualsDashboardWidgetSizingEnum.LARGE_ROW;
            default:
                return VisualsDashboardWidgetSizingEnum.SMALL;
        }
    }

    getRowsAndColsBySize(size: VisualsDashboardWidgetSizingEnum): { rows: number; cols: number } {
        switch (size) {
            case VisualsDashboardWidgetSizingEnum.SMALL:
                return { rows: 1, cols: 1 };
            case VisualsDashboardWidgetSizingEnum.MEDIUM_ROW:
                return { rows: 1, cols: 2 };
            case VisualsDashboardWidgetSizingEnum.LARGE_ROW:
                return { rows: 1, cols: 3 };
            default:
                return { rows: 1, cols: 2 };
        }
    }

    mapSizeToDimensions(size: VisualsDashboardWidgetSizingEnum): string {
        switch (size) {
            case VisualsDashboardWidgetSizingEnum.SMALL:
                return '1x1';
            case VisualsDashboardWidgetSizingEnum.MEDIUM_ROW:
                return '1x2';
            case VisualsDashboardWidgetSizingEnum.LARGE_ROW:
                return '1x3';
            default:
                return '';
        }
    }
}
